import { createListenerMiddleware } from '@reduxjs/toolkit';
import { monitoring } from 'common/services';

import { DeviceKind } from '../core';
import { type DeviceRecoveryTracker, createDeviceRecoveryTracker } from '../monitoring';
import { acquireDevicesThunk } from './acquireDevices.thunk';
import { devicePermissionResolved } from './devices.slice';

import type { RootState } from 'store';

const newEpisodeId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  } catch {
    // fall through to the non-crypto fallback below
  }
  // Non-secure-context / old-browser fallback. Uniqueness only needs to hold within
  // a session for joining a failure to its recovery, so this is sufficient.
  return `dev-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
};

/**
 * The single home for device acquisition telemetry. Every resolved outcome flows
 * through one recovery tracker, which emits the joinable failure→recovery signal to
 * DataDog. This replaces the per-component, ad-hoc transition tracking scattered
 * across the codebase.
 *
 * The tracker is a session-scoped singleton so a failure recorded now can be joined
 * to a recovery that happens later (e.g. the user plugs in a mic mid-round).
 */
export const deviceRecoveryTracker: DeviceRecoveryTracker = createDeviceRecoveryTracker({
  track: (action, extra) => monitoring.trackAction(action, extra),
  now: () => Date.now(),
  newEpisodeId,
});

export const deviceTelemetryListener = createListenerMiddleware();

deviceTelemetryListener.startListening({
  actionCreator: acquireDevicesThunk.fulfilled,
  effect: ({ payload }) => {
    (Object.keys(payload) as DeviceKind[]).forEach((kind) => {
      const outcome = payload[kind];
      if (outcome !== undefined) deviceRecoveryTracker.recordOutcome(kind, outcome);
    });
  },
});

// Emit a one-shot signal the first time each kind becomes granted (resolution or acquisition).
deviceTelemetryListener.startListening({
  actionCreator: devicePermissionResolved,
  effect: ({ payload: { kind, status } }, { getOriginalState }) => {
    const previous = (getOriginalState() as RootState).devices[
      kind === 'microphone' ? 'microphonePermission' : 'cameraPermission'
    ];
    if (status === 'granted' && previous !== 'granted') {
      monitoring.trackAction(`devices.${kind}.permission_granted`);
    }
  },
});
